import { FC, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { UseMutationResult } from '@tanstack/react-query';
import {
  KidTemplateAffectedShareClass,
  KidTemplateSetupStatus,
  KidTemplateType,
} from '../../../../../api/kids/types';
import { useAllShareClasses } from '../../../../../api/shareclass';
import {
  btnDanger,
  btnPrimary,
  btnSecondary,
  listToolbarDropdownClassName,
  listToolbarDropdownMenuClassName,
} from '../templateManagement.constants';
import {
  AffectedDocumentsQueryLike,
  AddTemplateToDocumentContext,
  TemplateReferenceOptions,
} from '../templateManagement.types';
import GenericModal from '../../../../ui/GenericModal';
import GenericDropdown from '../../../../ui/GenericDropDown';
import RaptorToolTip from '../../../../ui/RaptorTooltip';
import SearchBar from '../../../../ui/inputs/SearchBar';
import AffectedShareClassesList from './AffectedShareClassesList';
import TemplateContentEditor from './TemplateContentEditor';
import TemplateDocumentMeta from './TemplateDocumentMeta';
import TemplateEditDocumentPreview from './TemplateEditDocumentPreview';
import TemplateTypeChip from './TemplateTypeChip';
import TemplateSetupStatusPills from './TemplateSetupStatusPills';
import EditTemplateDetailsModal from './EditTemplateDetailsModal';
import RemoveTemplateFromDocumentModal from './RemoveTemplateFromDocumentModal';
import type { FormatOptions } from '../../../raidr/kidsV2/types/KidPdfGeneratorV2.types';
import {
  EMPTY_KID_TEMPLATE_AFFECTED_ROWS,
  enrichKidTemplateDocumentRowsWithFunds,
  enrichKidTemplateDocumentRowsWithShareClasses,
  formatKidTemplateFundName,
  formatKidTemplateShareClassLabel,
  formatKidTemplateShareClassName,
  KidTemplateDocumentCoverageState,
  KidTemplateDocumentRow,
  kidTemplateDocumentCoverageBadgeClass,
  kidTemplateDocumentCoverageChipLabel,
  kidTemplateDocumentRowKey,
  mergeKidTemplateDocumentRows,
} from '../templateDocumentRows.utils';
import useConfirmation from '../../../../../hooks/useConfirmation';
import { formatOptionsForPersistence } from '../templateManagement.utils';

type EditTemplateFormValues = {
  content: string;
  formatOptions: FormatOptions;
};

type AffectedFundGroup = {
  fundId: string;
  fundLabel: string;
  rows: KidTemplateDocumentRow[];
};

const ALL_COVERAGE_FILTER = 'all';

type CoverageFilterOption = {
  value: typeof ALL_COVERAGE_FILTER | KidTemplateDocumentCoverageState;
  label: string;
};

const COVERAGE_FILTER_OPTIONS: CoverageFilterOption[] = [
  { value: ALL_COVERAGE_FILTER, label: 'All statuses' },
  {
    value: 'present',
    label: kidTemplateDocumentCoverageChipLabel.present,
  },
  {
    value: 'missing',
    label: kidTemplateDocumentCoverageChipLabel.missing,
  },
  {
    value: 'invalid',
    label: kidTemplateDocumentCoverageChipLabel.invalid,
  },
];

const CoverageFilterLabel: FC<{
  status: CoverageFilterOption['value'];
  label: string;
}> = ({ status, label }) => {
  if (status === ALL_COVERAGE_FILTER) {
    return <>{label}</>;
  }

  return (
    <span
      className={`inline-flex whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium ring-1 ${kidTemplateDocumentCoverageBadgeClass[status]}`}
    >
      {label}
    </span>
  );
};

const TemplateAffectedDocumentsConfirmList: FC<{
  groups: AffectedFundGroup[];
}> = ({ groups }) => {
  const totalCount = groups.reduce((sum, group) => sum + group.rows.length, 0);

  if (totalCount === 0) {
    return (
      <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
        No documents currently reference this template.
      </p>
    );
  }

  return (
    <div className="overflow-hidden rounded-md border border-gray-200 bg-gray-50">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-3 py-2">
        <p className="text-xs font-semibold text-gray-800">Documents</p>
        <span className="inline-flex items-baseline rounded-md bg-white px-1.5 py-0.5 text-xs font-semibold tabular-nums text-gray-700 ring-1 ring-gray-200">
          {totalCount} document{totalCount === 1 ? '' : 's'}
        </span>
      </div>
      <div className="max-h-48 overflow-y-auto px-3 py-2">
        {groups.map((group) => (
          <div key={group.fundId} className="mt-2 first:mt-0">
            <p className="text-sm font-semibold text-gray-800">
              {group.fundLabel}
            </p>
            <ul className="mt-0.5 list-disc space-y-0.5 pl-5 text-sm text-gray-700">
              {group.rows.map((row) => (
                <li
                  key={kidTemplateDocumentRowKey(row)}
                  className="leading-snug marker:text-gray-400"
                >
                  {formatKidTemplateShareClassLabel(row)}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

const EditTemplatePanel: FC<
  TemplateReferenceOptions & {
    clientId: string;
    templateName?: string;
    scopeSubtitle?: string | null;
    selectedTemplateId: string;
    initialContent: string;
    initialFormatOptions: FormatOptions;
    allowedDowngradeTypes: KidTemplateType[];
    currentTemplateTypeForRank: KidTemplateType | null;
    templateSetupStatus: string | null;
    missingDocumentCount: number;
    invalidDocumentCount: number;
    isCheckingDocumentCoverage: boolean;
    canUpdateContent: boolean;
    missingDocuments: KidTemplateAffectedShareClass[];
    invalidDocuments: KidTemplateAffectedShareClass[];
    affectedDocumentsQuery: AffectedDocumentsQueryLike;
    addTemplateContext?: AddTemplateToDocumentContext;
    updateTemplateContentMutation: UseMutationResult<any, any, any>;
    downgradeTemplateMutation: UseMutationResult<any, any, any>;
    deactivateTemplateMutation: UseMutationResult<any, any, any>;
    onDeactivated?: () => void;
  }
> = ({
  clientId,
  templateName,
  scopeSubtitle,
  selectedTemplateId,
  initialContent,
  initialFormatOptions,
  allowedDowngradeTypes,
  currentTemplateTypeForRank,
  templateSetupStatus,
  missingDocumentCount,
  invalidDocumentCount,
  isCheckingDocumentCoverage,
  canUpdateContent,
  missingDocuments,
  invalidDocuments,
  affectedDocumentsQuery,
  addTemplateContext,
  updateTemplateContentMutation,
  downgradeTemplateMutation,
  deactivateTemplateMutation,
  onDeactivated,
  ...referenceOptions
}) => {
    const { handleConfirmation } = useConfirmation();
    const { data: allShareClasses } = useAllShareClasses({ client_id: clientId });
    const [previewDocument, setPreviewDocument] =
      useState<KidTemplateDocumentRow | null>(null);
    const [removeDocument, setRemoveDocument] =
      useState<KidTemplateDocumentRow | null>(null);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [affectedDocumentsSearch, setAffectedDocumentsSearch] = useState('');
    const [affectedDocumentsStatusFilter, setAffectedDocumentsStatusFilter] =
      useState<CoverageFilterOption['value']>(ALL_COVERAGE_FILTER);
    const {
      control,
      watch,
      setValue,
      getValues,
      reset,
      formState: { isDirty },
    } = useForm<EditTemplateFormValues>({
      defaultValues: {
        content: initialContent,
        formatOptions: initialFormatOptions,
      },
    });

    useEffect(() => {
      reset({
        content: initialContent,
        formatOptions: initialFormatOptions,
      });
    }, [selectedTemplateId, initialContent, initialFormatOptions, reset]);

    const previewContent = watch('content');
    const previewFormatOptions = watch('formatOptions');

    const affectedDocumentRows = useMemo(
      () =>
        enrichKidTemplateDocumentRowsWithFunds(
          enrichKidTemplateDocumentRowsWithShareClasses(
            mergeKidTemplateDocumentRows(
              affectedDocumentsQuery.data ?? EMPTY_KID_TEMPLATE_AFFECTED_ROWS,
              missingDocuments,
              invalidDocuments,
            ),
            allShareClasses,
          ),
          referenceOptions.availableFunds,
        ),
      [
        affectedDocumentsQuery.data,
        missingDocuments,
        invalidDocuments,
        allShareClasses,
        referenceOptions.availableFunds,
      ],
    );

    const selectedCoverageFilter =
      COVERAGE_FILTER_OPTIONS.find(
        (option) => option.value === affectedDocumentsStatusFilter,
      ) ?? COVERAGE_FILTER_OPTIONS[0];

    const affectedDocumentsByFund = useMemo(() => {
      const byFund = new Map<
        string,
        { fundId: string; fundLabel: string; rows: typeof affectedDocumentRows }
      >();
      affectedDocumentRows.forEach((row) => {
        const existing = byFund.get(row.fund_id);
        if (existing) {
          existing.rows.push(row);
        } else {
          byFund.set(row.fund_id, {
            fundId: row.fund_id,
            fundLabel: formatKidTemplateFundName(row),
            rows: [row],
          });
        }
      });
      return [...byFund.values()]
        .sort((a, b) => a.fundLabel.localeCompare(b.fundLabel))
        .map((group) => ({
          ...group,
          rows: group.rows.sort((a, b) =>
            formatKidTemplateShareClassName(a).localeCompare(
              formatKidTemplateShareClassName(b),
            ),
          ),
        }));
    }, [affectedDocumentRows]);

    const isIncompleteTemplate =
      templateSetupStatus === KidTemplateSetupStatus.INCOMPLETE;
    const isInvalidTemplate =
      templateSetupStatus === KidTemplateSetupStatus.INVALID;
    const lockNonReadyActions = isIncompleteTemplate || isInvalidTemplate;
    const updateContentTooltip = isInvalidTemplate
      ? 'Remove this template from documents it should not appear on before editing content.'
      : isIncompleteTemplate
        ? 'Add this template to every document it should appear on before editing content.'
        : '';
    const contentCardTone = isInvalidTemplate
      ? {
        section: 'rounded-lg border border-rose-200 bg-rose-50 p-3',
        title: 'text-sm font-semibold text-rose-950',
        dot: 'text-rose-300',
        helper: 'text-xs leading-snug text-rose-800',
        chip: 'inline-flex items-baseline rounded-md bg-white/80 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-rose-950 ring-1 ring-rose-300/80',
      }
      : isIncompleteTemplate
        ? {
          section: 'rounded-lg border border-amber-200 bg-amber-50 p-3',
          title: 'text-sm font-semibold text-amber-950',
          dot: 'text-amber-300',
          helper: 'text-xs leading-snug text-amber-800',
          chip: 'inline-flex items-baseline rounded-md bg-white/80 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-amber-950 ring-1 ring-amber-300/80',
        }
        : {
          section: 'rounded-lg border border-gray-200 bg-gray-50/70 p-3',
          title: 'text-sm font-semibold text-gray-900',
          dot: 'text-gray-300',
          helper: 'text-xs leading-snug text-gray-500',
          chip: '',
        };
    const contentLockCopy = isInvalidTemplate
      ? 'Content is locked until this template is removed from documents it should not appear on.'
      : isIncompleteTemplate
        ? 'Content is locked until this template is on every document it should appear on.'
        : 'Changes apply to every document that uses this template.';
    const coverageChips: string[] = [];
    if (
      !isCheckingDocumentCoverage &&
      (isIncompleteTemplate || isInvalidTemplate)
    ) {
      const missingLabel =
        missingDocumentCount > 0 ? `${missingDocumentCount} missing` : null;
      const invalidLabel =
        invalidDocumentCount > 0 ? `${invalidDocumentCount} invalid` : null;
      coverageChips.push(
        ...((isInvalidTemplate
          ? [invalidLabel, missingLabel]
          : [missingLabel, invalidLabel]
        ).filter((label): label is string => Boolean(label))),
      );
    }

    const handleUpdateContent = () => {
      handleConfirmation(
        'Update template content',
        <div className="space-y-3">
          <p>
            Updating this template changes the layout of every affected document
            and increases their version numbers. Review the document preview first.
          </p>
          <TemplateAffectedDocumentsConfirmList groups={affectedDocumentsByFund} />
        </div>,
        () => {
          const { content, formatOptions } = getValues();
          updateTemplateContentMutation.mutate(
            {
              updated_content: content,
              format_options: formatOptionsForPersistence(formatOptions),
            },
            {
              onSuccess: () => {
                reset({ content, formatOptions });
              },
            },
          );
        },
      );
    };

    const handleDeactivate = () => {
      handleConfirmation(
        'Deactivate template',
        <div className="space-y-3">
          <p>
            Deactivating this template removes it from the documents below and
            increases their version numbers.
          </p>
          <TemplateAffectedDocumentsConfirmList groups={affectedDocumentsByFund} />
        </div>,
        () =>
          deactivateTemplateMutation.mutate(undefined, {
            onSuccess: () => onDeactivated?.(),
          }),
        { confirmColor: 'error' },
      );
    };

    return (
      <div className="flex h-full min-h-0 flex-col">
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
          {/* Identity strip */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
                <h2 className="text-2xl font-semibold tracking-tight text-gray-900">
                  {templateName || selectedTemplateId}
                </h2>
                {currentTemplateTypeForRank ? (
                  <TemplateTypeChip
                    templateType={currentTemplateTypeForRank}
                    size="md"
                    tooltip={scopeSubtitle}
                  />
                ) : null}
                <TemplateSetupStatusPills
                  setupStatus={templateSetupStatus}
                  missingCount={missingDocumentCount}
                  invalidCount={invalidDocumentCount}
                />
              </div>
            </div>
            <button
              type="button"
              className={btnSecondary}
              onClick={() => setIsDetailsModalOpen(true)}
            >
              Edit details
            </button>
          </div>

          <section className={contentCardTone.section}>
            <div className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 gap-y-1">
              <h3 className={contentCardTone.title}>Content</h3>
              <span className={contentCardTone.dot} aria-hidden>
                ·
              </span>
              <p className={contentCardTone.helper}>{contentLockCopy}</p>
              {coverageChips.map((label) => (
                <span key={label} className={contentCardTone.chip}>
                  {label}
                </span>
              ))}
            </div>

            <div className="mt-3">
              <Controller
                name="content"
                control={control}
                render={({ field }) => (
                  <TemplateContentEditor
                    key={selectedTemplateId}
                    value={field.value}
                    onChange={field.onChange}
                    formatOptions={watch('formatOptions')}
                    onFormatOptionsChange={(value) =>
                      setValue('formatOptions', value, { shouldDirty: true })
                    }
                    disabled={!canUpdateContent}
                    minHeightClass="min-h-[10rem]"
                    className="shadow-none"
                  />
                )}
              />
            </div>

            <div className="mt-3">
              <span className="group relative inline-flex">
                {updateContentTooltip ? (
                  <RaptorToolTip
                    text={updateContentTooltip}
                    className="left-0 w-max translate-x-0"
                  />
                ) : null}
                <button
                  type="button"
                  className={btnPrimary}
                  onClick={handleUpdateContent}
                  disabled={
                    updateTemplateContentMutation.isPending ||
                    !canUpdateContent ||
                    !isDirty
                  }
                >
                  {updateTemplateContentMutation.isPending
                    ? 'Updating…'
                    : 'Update Content'}
                </button>
              </span>
            </div>
          </section>

          <section
            id="template-affected-documents"
            className="rounded-lg border border-gray-200 bg-gray-50/70 p-3"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
                <h3 className="text-sm font-semibold text-gray-900">
                  Documents
                </h3>
                {!affectedDocumentsQuery.isLoading &&
                  !affectedDocumentsQuery.isError ? (
                  <span className="inline-flex items-baseline rounded-md bg-white px-1.5 py-0.5 text-xs font-semibold tabular-nums text-gray-700 ring-1 ring-gray-200">
                    {affectedDocumentRows.length} document
                    {affectedDocumentRows.length === 1 ? '' : 's'}
                  </span>
                ) : null}
              </div>
              {!affectedDocumentsQuery.isLoading &&
                !affectedDocumentsQuery.isError &&
                affectedDocumentRows.length > 0 ? (
                <div className="flex items-center gap-2">
                  <SearchBar
                    id="affected-documents-search"
                    searchHandler={setAffectedDocumentsSearch}
                    placeholder="Search documents…"
                    className="w-64 shrink-0"
                  />
                  <div className="inline-flex shrink-0">
                    <GenericDropdown
                      data={COVERAGE_FILTER_OPTIONS}
                      selected={selectedCoverageFilter}
                      setSelected={(option) =>
                        setAffectedDocumentsStatusFilter(option.value)
                      }
                      getDisplayValue={(option) => option.label}
                      getDisplayNode={(option) => (
                        <CoverageFilterLabel
                          status={option.value}
                          label={option.label}
                        />
                      )}
                      label={
                        <CoverageFilterLabel
                          status={selectedCoverageFilter.value}
                          label={selectedCoverageFilter.label}
                        />
                      }
                      truncateLabel={
                        selectedCoverageFilter.value === ALL_COVERAGE_FILTER
                      }
                      tooltip="Filter by status"
                      className={listToolbarDropdownClassName}
                      dropdownClassName={listToolbarDropdownMenuClassName}
                    />
                  </div>
                </div>
              ) : null}
            </div>
            <div className="mt-3">
              {affectedDocumentsQuery.isLoading ? (
                <p className="text-sm text-gray-500">Loading documents…</p>
              ) : affectedDocumentsQuery.isError ? (
                <p className="text-sm text-rose-700">
                  Could not load documents.
                </p>
              ) : (
                <AffectedShareClassesList
                  clientId={clientId}
                  documentRows={affectedDocumentRows}
                  addTemplateContext={addTemplateContext}
                  emptyLabel="No documents reference this template."
                  variant="page"
                  searchQuery={affectedDocumentsSearch}
                  statusFilter={affectedDocumentsStatusFilter}
                  className="rounded-md border border-gray-200 bg-white"
                  onPreviewDocument={setPreviewDocument}
                  onRemoveDocument={setRemoveDocument}
                />
              )}
            </div>
          </section>

          <section className="rounded-lg border border-gray-200 bg-gray-50/70 p-3">
            <button
              type="button"
              className={btnDanger}
              onClick={handleDeactivate}
              disabled={deactivateTemplateMutation.isPending}
            >
              {deactivateTemplateMutation.isPending
                ? 'Deactivating…'
                : 'Deactivate'}
            </button>
          </section>
        </div>

        <GenericModal
          isOpen={!!previewDocument}
          setIsOpen={(open) => {
            if (!open) setPreviewDocument(null);
          }}
          title="Template preview"
          className="max-w-5xl"
        >
          {previewDocument ? (
            <div className="flex h-[70vh] min-h-[24rem] flex-col">
              <TemplateDocumentMeta
                templateName={templateName}
                document={previewDocument}
              />
              <div className="min-h-0 flex-1">
                <TemplateEditDocumentPreview
                  key={kidTemplateDocumentRowKey(previewDocument)}
                  clientId={clientId}
                  templateId={selectedTemplateId}
                  templateName={templateName}
                  content={previewContent}
                  formatOptions={previewFormatOptions}
                  templateType={currentTemplateTypeForRank}
                  document={previewDocument}
                  className="h-full"
                />
              </div>
            </div>
          ) : null}
        </GenericModal>

        {removeDocument && addTemplateContext ? (
          <RemoveTemplateFromDocumentModal
            isOpen
            onClose={() => setRemoveDocument(null)}
            document={removeDocument}
            {...addTemplateContext}
          />
        ) : null}

        <EditTemplateDetailsModal
          isOpen={isDetailsModalOpen}
          onClose={() => setIsDetailsModalOpen(false)}
          clientId={clientId}
          templateId={selectedTemplateId}
          templateName={templateName || ''}
          currentTemplateType={currentTemplateTypeForRank}
          currentScopeLabel={scopeSubtitle}
          allowedDowngradeTypes={allowedDowngradeTypes}
          templateSetupStatus={templateSetupStatus}
          lockScopeChanges={lockNonReadyActions}
          downgradeMutation={downgradeTemplateMutation}
          {...referenceOptions}
        />
      </div>
    );
  };

export default EditTemplatePanel;
